import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import PromptCard from "./PromptCard";
import {
  CHAT_PROMPT_CUSTOM_TYPE,
  CUSTOM_PROMPT_ENTRY,
  MANAGING_PROMPT_CATEGORIES,
  filterPromptsForContext,
  isPromptUsable,
} from "../constants/chatConstants";
import type { ManagingPromptCategory, PromptGalleryProps } from "../types/chat.types";

type CategoryFilter = "all" | ManagingPromptCategory;

const PromptGallery: React.FC<PromptGalleryProps> = ({
  prompts,
  recordType,
  onSelectPrompt,
  onSelectCustom,
  isLoading = false,
  className = "",
  hasCallRecordingWithTranscript,
}) => {
  const { t } = useTranslation();
  const [category, setCategory] = useState<CategoryFilter>("all");

  const visiblePrompts = useMemo(() => {
    const filtered = filterPromptsForContext(prompts, { recordType, category });
    const ordered = filtered.filter((p) => p.promptType !== CHAT_PROMPT_CUSTOM_TYPE);
    const customEntry =
      filtered.find((p) => p.promptType === CHAT_PROMPT_CUSTOM_TYPE) ?? CUSTOM_PROMPT_ENTRY;
    return [...ordered, customEntry];
  }, [prompts, recordType, category]);

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <CategoryTab
          label={t("All")}
          active={category === "all"}
          onClick={() => setCategory("all")}
        />
        {MANAGING_PROMPT_CATEGORIES.map((cat) => (
          <CategoryTab
            key={cat}
            label={t(cat[0].toUpperCase() + cat.slice(1))}
            active={category === cat}
            onClick={() => setCategory(cat)}
          />
        ))}
      </div>

      {isLoading ? (
        <SkeletonGrid />
      ) : visiblePrompts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
          {t("No prompts available for this record type yet.")}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {visiblePrompts.map((prompt) => {
            const { usable, reason, setupReason } = isPromptUsable(prompt, recordType, {
              hasCallRecordingWithTranscript,
            });
            const isCustom = prompt.promptType === CHAT_PROMPT_CUSTOM_TYPE;
            return (
              <PromptCard
                key={prompt.promptType}
                prompt={prompt}
                disabled={!usable}
                disabledReason={reason}
                setupReason={setupReason}
                onClick={() => {
                  if (isCustom) {
                    onSelectCustom?.();
                  } else {
                    onSelectPrompt(prompt);
                  }
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const CategoryTab: React.FC<{ label: React.ReactNode; active: boolean; onClick: () => void }> = ({
  label,
  active,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full px-2.5 py-1 text-xs transition-colors ${
      active
        ? "bg-gray-900 text-white"
        : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
    }`}
  >
    {label}
  </button>
);

const SkeletonGrid: React.FC = () => (
  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" aria-hidden="true">
    {Array.from({ length: 6 }).map((_, idx) => (
      <div
        key={idx}
        className="flex h-24 animate-pulse flex-col gap-2 rounded-xl border border-gray-200 bg-white p-3"
      >
        <div className="h-3 w-1/2 rounded bg-gray-200" />
        <div className="h-2 w-full rounded bg-gray-200" />
        <div className="h-2 w-4/5 rounded bg-gray-200" />
      </div>
    ))}
  </div>
);

export default PromptGallery;
